import { useEffect, useRef } from "react";
import { isSessionExpired, shouldAttemptSessionRefresh } from "../auth/sessionExpiry";

type ExpiryHandler = (expired: boolean) => void;
type RefreshHandler = () => Promise<boolean>;

/**
 * Tenta renovar a sessão antes do logout.
 * `onRefresh` deve retornar true se renovou com sucesso.
 */
export function useSessionExpiryGuard(
  onExpired: ExpiryHandler,
  onRefresh?: RefreshHandler
) {
  const onExpiredRef = useRef(onExpired);
  const onRefreshRef = useRef(onRefresh);
  onExpiredRef.current = onExpired;
  onRefreshRef.current = onRefresh;

  const refreshingRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (refreshingRef.current) return;

      const needsRefresh = shouldAttemptSessionRefresh();
      const expired = isSessionExpired();

      if ((needsRefresh || expired) && onRefreshRef.current) {
        refreshingRef.current = true;
        try {
          const renewed = await onRefreshRef.current();
          if (renewed) return;
        } finally {
          refreshingRef.current = false;
        }
      }

      if (isSessionExpired()) {
        onExpiredRef.current(true);
      }
    };

    void check();

    const intervalId = window.setInterval(() => {
      void check();
    }, 1000);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void check();
      }
    };

    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
